/**
 * The dashboard, and the vocabularies shared across screens.
 */
import { z } from 'zod';
import {
	auditStatusSchema,
	classificationSchema,
	roleSchema,
	severitySchema,
} from './enums';

export const dashboardOverviewSchema = z.object({
	counts: z.object({
		clients: z.number(),
		activeAudits: z.number(),
		filesProcessing: z.number(),
		filesReady: z.number(),
		openExceptions: z.number(),
		blockingExceptions: z.number(),
	}),
	attention: z.array(
		z.object({
			kind: z.string(),
			severity: severitySchema,
			count: z.number(),
			label: z.string(),
		}),
	),
	audits: z.array(
		z.object({
			pkAuditId: z.string(),
			name: z.string(),
			clientName: z.string(),
			status: auditStatusSchema,
			openExceptions: z.number(),
		}),
	),
});

export type DashboardOverview = z.infer<typeof dashboardOverviewSchema>;

/* ── events, firm configuration ─────────────────────────────────────────── */
//
// Prisma rows, not endpoint responses reshaped for a screen: names and
// nullability mirror `backend/prisma/schema.prisma` field-for-field, so there
// is no snake/camel translation to account for here.

/** One entry in a file's timeline — what FileDetail's history panel prints. */
export const fileEventSchema = z.object({
	pkFileEventId: z.string(),
	fkFileId: z.string(),
	event: z.string(),
	note: z.string(),
	fkActorId: z.string().nullable(),
	createdAt: z.string(),
});

/** One entry in an exception's history — who did what and why, independent of
 *  the analysis figures that raised it. */
export const exceptionEventSchema = z.object({
	pkExceptionEventId: z.string(),
	fkExceptionId: z.string(),
	action: z.string(),
	note: z.string(),
	fkActorId: z.string().nullable(),
	/** The person who acted, by name; null for an automatic action. */
	actorName: z.string().nullable().catch(null),
	createdAt: z.string(),
});

/** A spelling this firm has taught the product for a canonical field, so the
 *  next file that spells a column that way maps itself. */
export const fieldAliasSchema = z.object({
	pkFieldAliasId: z.string(),
	fkFirmId: z.string(),
	canonicalField: z.string(),
	spelling: z.string(),
	fkCreatedById: z.string().nullable(),
	createdAt: z.string(),
});

/** A versioned pack of classification rules. Never edited in place — an edit
 *  creates a new version, so a report that cited v2 still means v2. */
export const rulesetSchema = z.object({
	pkRulesetId: z.string(),
	fkFirmId: z.string().nullable(),
	process: z.string(),
	version: z.number(),
	status: z.enum(['DRAFT', 'SIMULATED', 'ACTIVE', 'SUPERSEDED']),
	note: z.string(),
	effectiveFrom: z.string(),
	createdAt: z.string(),
});

/** What a row IS, one rule at a time. A client-scoped rule beats a
 *  firm-general one at equal priority. */
export const classificationRuleSchema = z.object({
	pkClassificationRuleId: z.string(),
	fkRulesetId: z.string(),
	fkClientId: z.string().nullable(),
	priority: z.number(),
	matchField: z.string(),
	matchOperator: z.string(),
	matchValue: z.string(),
	classification: classificationSchema,
	reason: z.string(),
	createdAt: z.string(),
});

/** An outstanding invitation. Consumed by `/auth/accept-invite`. */
export const inviteSchema = z.object({
	pkInviteId: z.string(),
	fkFirmId: z.string(),
	email: z.string(),
	role: roleSchema,
	token: z.string(),
	fkInvitedById: z.string().nullable(),
	expiresAt: z.string(),
	acceptedAt: z.string().nullable(),
	createdAt: z.string(),
});

export type FileEvent = z.infer<typeof fileEventSchema>;
export type ExceptionEvent = z.infer<typeof exceptionEventSchema>;
export type FieldAlias = z.infer<typeof fieldAliasSchema>;
export type Ruleset = z.infer<typeof rulesetSchema>;
export type ClassificationRule = z.infer<typeof classificationRuleSchema>;
export type Invite = z.infer<typeof inviteSchema>;
